import { TemplatesService } from '@beeboard/common';
import { ActivityTemplateCompanyActivityReplaceRequest } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useTemplateStore } from '@/store/templateStore';
import { useActivityTemplateRulesStore } from '../activitytemplateRulesStore';
import { useCompanyActivityStore } from '../companyActivityStore';
import type {
  ActivitiesTemplateUpdateRequest,
  ActivityDocumentTemplate,
  ActivityDuplicateRequest,
  ActivityTemplate,
  ActivityTemplateCreateRequest,
  ActivityTemplateLinkRequest,
  ActivityTemplateSummary,
  ActivityTemplateTagCreate,
  ActivityTemplateUpdate,
  CompletionCriterionTemplate,
  CompletionCriterionTemplateCreate,
  CompletionCriterionTemplateUpdate,
  CreateCompanyActivityRequest,
  DocumentTemplate,
  LinkTemplate,
  ProjectFunction,
  TagTemplate,
  UpdateActivityDocumentTemplate,
} from '@beeboard/common';

export interface EnrichedActivityDocument {
  id: string;
  activityDocument: ActivityDocumentTemplate;
  document: DocumentTemplate;
  inputActivity: ActivityTemplateSummary | null;
}

export type ActivityTemplateStoreState = {
  templates: Record<string, ActivityTemplate[]>;
};

export const useActivityTemplateStore = defineStore('activityTemplateStore', {
  state: (): ActivityTemplateStoreState => ({
    templates: {},
  }),

  getters: {
    activity(state) {
      return (templateId: string, activityId: string): ActivityTemplate => {
        const template = state.templates[templateId];
        if (!template) {
          throw new Error('Missing template in store');
        }
        const activity = template.find((act) => act.id === activityId);
        if (!activity) {
          throw new Error('Missing activity in store');
        }

        return activity;
      };
    },

    hasActivity(state) {
      return (templateId: string, activityId: string): boolean => {
        const template = state.templates[templateId];
        if (!template) {
          return false;
        }

        return !!template.some((act) => act.id === activityId);
      };
    },

    responsibleFunction() {
      return (templateId: string, activityId: string) => {
        const activity = this.activity(templateId, activityId);
        const responsibleId = activity?.responsible;

        return responsibleId || null;
      };
    },

    predecessors() {
      return (templateId: string, activityId: string) => {
        const templateStore = useTemplateStore();
        const activity = this.activity(templateId, activityId);

        return activity.predecessors
          .map((predecessorId) =>
            templateStore.activitySummary(templateId, predecessorId),
          )
          .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));
      };
    },

    successors() {
      return (templateId: string, activityId: string) => {
        const templateStore = useTemplateStore();
        const activity = this.activity(templateId, activityId);

        return activity.successors
          .map((successorId) =>
            templateStore.activitySummary(templateId, successorId),
          )
          .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));
      };
    },

    potentialPredecessors() {
      return (templateId: string, activityId: string) => {
        const template = useTemplateStore().template(templateId);
        const activity = this.activity(templateId, activityId);
        const predecessors: ActivityTemplateSummary[] = [];
        const len = template.phases.length;
        let i = -1;
        while (++i < len) {
          const phaseId = template.phases[i].id;
          if (phaseId !== activity.phase) {
            const activitiesInPhase = template.activities.filter(
              ({ phase }) => phase === phaseId,
            );
            predecessors.push(...activitiesInPhase);
            continue;
          }
          break;
        }
        const activitiesInSamePhase = template.activities.filter(
          ({ id, position, phase }) =>
            phase === activity.phase &&
            id !== activityId &&
            position < activity.position,
        );
        predecessors.push(...activitiesInSamePhase);

        return predecessors.filter(
          ({ id }) => !activity.predecessors.includes(id),
        );
      };
    },

    availableSuccessors() {
      return (templateId: string, activityId: string) => {
        const template = useTemplateStore().template(templateId);
        const activity = this.activity(templateId, activityId);
        const successors: ActivityTemplateSummary[] = [];
        let len = template.phases.length;
        while (--len >= 0) {
          const phaseId = template.phases[len].id;
          if (phaseId !== activity.phase) {
            const activitiesInPhase = template.activities.filter(
              ({ phase }) => phase === phaseId,
            );
            successors.push(...activitiesInPhase);
            continue;
          }
          break;
        }
        const activitiesInSamePhase = template.activities.filter(
          ({ id, position, phase }) =>
            phase === activity.phase &&
            id !== activityId &&
            position > activity.position,
        );
        successors.push(...activitiesInSamePhase);

        return successors.filter(({ id }) => !activity.successors.includes(id));
      };
    },

    milestone() {
      return (templateId: string, activityId: string) => {
        const activity = this.activity(templateId, activityId);

        return activity.milestone;
      };
    },

    milestoneSummary() {
      return (templateId: string, activityId: string) => {
        const activity = this.activity(templateId, activityId);
        if (!activity.milestone) {
          return;
        }
        const templateStore = useTemplateStore();
        const milestones = templateStore.milestones(templateId);
        const milestone = milestones.find(
          ({ id }) => id === activity.milestone?.id,
        );

        return milestone;
      };
    },

    currentTags() {
      return (templateId: string, activityId: string): TagTemplate[] => {
        const templateStore = useTemplateStore();
        const ids = this.activity(templateId, activityId).tags;

        return ids
          .map((id) => templateStore.tag(templateId, id))
          .filter((tag) => tag)
          .sort((a, b) => a.title.localeCompare(b.title));
      };
    },

    currentTopicTags() {
      return (templateId: string, activityId: string): TagTemplate[] => {
        return this.currentTags(templateId, activityId).filter(
          (tag) => tag && tag.topic,
        );
      };
    },

    currentNonTopicTags() {
      return (templateId: string, activityId: string) => {
        const templateStore = useTemplateStore();
        const ids = this.activity(templateId, activityId)?.tags || [];

        return ids
          .map((id) => templateStore.tag(templateId, id))
          .filter((tag) => !tag?.topic)
          .sort((a, b) => a.title.localeCompare(b.title));
      };
    },

    links() {
      return (templateId: string, activityId: string) => {
        const templateStore = useTemplateStore();
        const activity = this.activity(templateId, activityId);

        return activity.links.map((id) => {
          const link = templateStore.link(templateId, id);
          if (!link) {
            throw new Error('Missing link in activityTemplateStore');
          }

          return link as LinkTemplate;
        });
      };
    },

    watchers() {
      return (templateId: string, activityId: string) => {
        const templateStore = useTemplateStore();
        const activity = this.activity(templateId, activityId);

        return activity.watchers.map((id) => {
          const watcher = templateStore.projectFunction(templateId, id);
          if (!watcher) {
            throw new Error('Missing watcher in activityTemplateStore');
          }

          return watcher;
        });
      };
    },

    availableFunctions() {
      return (templateId: string, activityId: string) => {
        const templateStore = useTemplateStore();
        const activity = this.activity(templateId, activityId);

        return templateStore
          .projectFunctions(templateId)
          .filter(
            ({ id }) =>
              id !== activity.responsible &&
              !activity.watchers.includes(id) &&
              !activity.approvers.includes(id),
          );
      };
    },

    approvers() {
      return (templateId: string, activityId: string): ProjectFunction[] => {
        const templateStore = useTemplateStore();
        const approverIds = this.activity(templateId, activityId).approvers;

        return approverIds.map((id) => {
          const approver = templateStore.projectFunction(templateId, id);
          if (!approver) {
            throw new Error('Missing function in template');
          }

          return approver;
        });
      };
    },

    enrichedActivityDocuments() {
      return (
        templateId: string,
        activityId: string,
      ): EnrichedActivityDocument[] => {
        const templateStore = useTemplateStore();
        const template = templateStore.template(templateId);
        const activity = this.activity(templateId, activityId);
        if (!activity || !template) {
          return [];
        }

        return activity.documents.map((activityDocument) => {
          const document = template.documents.find(
            (doc) => doc.id === activityDocument.id,
          );

          if (!document) {
            throw Error(
              `Can not get document with ID ${activityDocument.id} from the documentStore`,
            );
          }

          let inputActivity: ActivityTemplateSummary | null = null;
          if (activityDocument.input !== null) {
            inputActivity =
              templateStore.activitySummary(
                templateId,
                activityDocument.input,
              ) ?? null;

            if (!inputActivity) {
              throw Error(
                `Can not het input document with ID ${activityDocument.input} from the activityStore`,
              );
            }
          }

          return {
            id: document.id,
            activityDocument,
            document,
            inputActivity,
          };
        });
      };
    },

    criterion() {
      return (templateId: string, activityId: string, criterionId: string) => {
        const activity = this.activity(templateId, activityId);
        const criterion = activity.completion_criteria.find(
          ({ id }) => criterionId === id,
        );
        if (!criterion) {
          throw new Error('Missing completion criterion in activity template');
        }

        return criterion;
      };
    },
  },

  actions: {
    getTemplate(templateId: string) {
      if (!this.templates[templateId]) {
        this.templates[templateId] = [];
      }

      return this.templates[templateId];
    },

    writeActivityTags(
      templateId: string,
      activityId: string,
      options: { add?: string[]; remove?: string[] },
    ) {
      if (!this.hasActivity(templateId, activityId)) {
        return;
      }

      const activity = this.activity(templateId, activityId);
      const { add, remove } = options;

      if (add) {
        add.forEach((tagId) => {
          if (!activity.tags.includes(tagId)) {
            activity.tags.push(tagId);
          }
        });
      }

      if (remove) {
        activity.tags = activity.tags.filter(
          (tagId) => !remove.includes(tagId),
        );
      }
    },

    write(templateId: string, activity: ActivityTemplate) {
      const activityTemplate = this.getTemplate(templateId);
      const oldActivity = activityTemplate.find(
        (act) => act.id === activity.id,
      );
      if (oldActivity) {
        Object.assign(oldActivity, activity);
      } else {
        activityTemplate.push(activity);
      }

      return this.activity(templateId, activity.id);
    },

    writeCriterion(
      templateId: string,
      activityId: string,
      payload: CompletionCriterionTemplate,
    ) {
      const activity = this.activity(templateId, activityId);
      const criterion = activity.completion_criteria.find(
        ({ id }) => id === payload.id,
      );

      if (criterion) {
        Object.assign(criterion, payload);
      } else {
        activity.completion_criteria.push(payload);
      }

      useTemplateStore().writeCompletionCriterion(
        templateId,
        activityId,
        payload,
      );

      return this.criterion(templateId, activityId, payload.id);
    },

    /**
     * Copies a criterion the map wrote into the template summary to the
     * activity's cached details, without sharing the object. The reorder
     * check reads these details, so a criterion missing here makes every
     * reorder fail (WI #5801). Nothing to do while the activity is not cached.
     */
    writeCachedCriterion(
      templateId: string,
      activityId: string,
      payload: CompletionCriterionTemplate,
    ) {
      if (!this.hasActivity(templateId, activityId)) {
        return;
      }
      const criteria = this.activity(
        templateId,
        activityId,
      ).completion_criteria;
      const criterion = criteria.find(({ id }) => id === payload.id);
      if (criterion) {
        Object.assign(criterion, payload);
      } else {
        criteria.push(structuredClone(toRaw(payload)));
      }
    },

    writeDocument(
      templateId: string,
      activityId: string,
      payload: ActivityDocumentTemplate,
    ) {
      const activity = this.activity(templateId, activityId);
      const doc = activity.documents.find(({ id }) => id === payload.id);
      if (doc) {
        Object.assign(doc, payload);
      } else {
        activity.documents.push(payload);
      }
      this.syncSummaryDocuments(templateId, activityId);
    },

    // Mirror the activity's documents into its map summary — a separate object
    // from the detail record, otherwise stale until a reload (and the
    // shared_document check reads from summaries).
    syncSummaryDocuments(templateId: string, activityId: string) {
      const templateStore = useTemplateStore();
      if (
        !templateStore.hasTemplate(templateId) ||
        !this.hasActivity(templateId, activityId)
      ) {
        return;
      }
      const summary = templateStore
        .template(templateId)
        .activities.find((activity) => activity.id === activityId);
      if (!summary) {
        return;
      }
      summary.documents = this.activity(templateId, activityId).documents.map(
        ({ id, input, output }) => ({
          id,
          input: input ?? null,
          output: output ?? false,
        }),
      );
      summary.documentsIds = summary.documents.map((doc) => doc.id);
    },

    async add(templateId: string, newActivity: ActivityTemplateCreateRequest) {
      const errors = useActivityTemplateRulesStore().checkAddActivity(
        templateId,
        newActivity,
      );
      if (errors) {
        throw errors;
      }

      return TemplatesService.addActivity(templateId, newActivity).then(
        async ({ activity }) => {
          if (newActivity.company_activity_id) {
            await useTemplateStore().loadTemplate(templateId);
          } else {
            useTemplateStore().writeActivitySummary(
              templateId,
              structuredClone(activity),
            );
          }

          return this.write(templateId, activity);
        },
      );
    },

    async loadActivity(templateId: string, activityId: string) {
      const { activity } = await TemplatesService.getActivity(
        templateId,
        activityId,
      );
      this.write(templateId, activity);

      return this.activity(templateId, activityId);
    },

    /**
     * Refetches an activity the AI assistant just saved, for the map and the
     * list (WI #5767): its summary always, its details only when they are
     * cached already — `reloadCachedActivities` reloads those one by one.
     */
    async refreshActivity(templateId: string, activityId: string) {
      const { activity } = await TemplatesService.getActivity(
        templateId,
        activityId,
      );
      if (this.hasActivity(templateId, activityId)) {
        this.write(templateId, activity);
      }
      useTemplateStore().writeActivitySummary(
        templateId,
        structuredClone(activity),
      );
    },

    /**
     * Refetches every cached activity of the template — the details the map
     * sidebar shows (documents, links, approvers, relations) — and drops the
     * ones that no longer load. The AI assistant changes them without going
     * through this store (WI #5747).
     */
    async reloadCachedActivities(templateId: string) {
      const activityIds = (this.templates[templateId] ?? []).map(
        ({ id }) => id,
      );

      await Promise.all(
        activityIds.map((activityId) =>
          this.loadActivity(templateId, activityId).catch(() => {
            this.templates[templateId] = this.getTemplate(templateId).filter(
              ({ id }) => id !== activityId,
            );
          }),
        ),
      );
    },

    async update(
      templateId: string,
      activityId: string,
      payload: ActivityTemplateUpdate,
    ) {
      const errors = useActivityTemplateRulesStore().checkUpdateActivity(
        templateId,
        activityId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const { activity } = await TemplatesService.updateActivity(
        templateId,
        activityId,
        payload,
      );
      this.write(templateId, activity);
      useTemplateStore().writeActivitySummary(templateId, activity);
    },

    async updateActivities(
      templateId: string,
      payload: ActivitiesTemplateUpdateRequest,
    ) {
      const activities = await TemplatesService.updateActivities(
        templateId,
        payload,
      );
      activities.forEach((activity) => {
        this.write(templateId, activity);
      });
      await useTemplateStore().loadTemplate(templateId);
    },

    /**
     * A multi-move (WI #5795): `activity_ids` move by `offset` days in one
     * request, all or none. Other activities' relations may change with a
     * forced move, so the template reloads, as on a project — which the bulk
     * update does every time.
     */
    async updatePositions(
      templateId: string,
      payload: ActivitiesTemplateUpdateRequest,
    ) {
      await this.updateActivities(templateId, payload);
    },

    async duplicate(templateId: string, payload: ActivityDuplicateRequest) {
      await TemplatesService.duplicateActivities(templateId, payload);
      const templateStore = useTemplateStore();
      await templateStore.loadTemplate(templateId);
    },

    async replace(
      templateId: string,
      activityId: string,
      payload: ActivityTemplateCompanyActivityReplaceRequest,
    ) {
      await TemplatesService.replaceCompanyActivity(
        templateId,
        activityId,
        payload,
      );
      const templateStore = useTemplateStore();
      await templateStore.loadTemplate(templateId);
    },

    async updatePosition(
      templateId: string,
      activityId: string,
      phase: string,
      position: number,
      forceUpdate = false,
    ) {
      const errors = useActivityTemplateRulesStore().checkUpdateActivity(
        templateId,
        activityId,
        { phase, position, force_update_position: forceUpdate },
      );
      if (errors) {
        throw errors;
      }
      const { activity } = await TemplatesService.updateActivity(
        templateId,
        activityId,
        { phase, position, force_update_position: forceUpdate },
      );
      const templateStore = useTemplateStore();
      if (!templateStore.hasTemplate(templateId)) {
        return;
      }
      if (forceUpdate) {
        await templateStore.loadTemplate(templateId);
      } else {
        const summary = templateStore.activitySummary(templateId, activityId);
        summary.phase = activity.phase;
        summary.position = activity.position;
      }
      this.write(templateId, activity);
    },

    async updateWorkstream(
      templateId: string,
      activityId: string,
      newWorkstreamId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkUpdateActivity(
        templateId,
        activityId,
        { workstream: newWorkstreamId },
      );
      if (errors) {
        throw errors;
      }
      const { activity } = await TemplatesService.updateActivity(
        templateId,
        activityId,
        { workstream: newWorkstreamId },
      );

      const templateStore = useTemplateStore();
      if (!templateStore.hasTemplate(templateId)) {
        return;
      }

      const summary = templateStore.activitySummary(templateId, activityId);
      summary.workstream = activity.workstream;
      this.write(templateId, activity);
    },

    async remove(templateId: string, activityId: string) {
      const errors = useActivityTemplateRulesStore().checkRemoveActivity(
        templateId,
        activityId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.deleteActvity(templateId, activityId);
      await useTemplateStore().loadTemplate(templateId);
    },

    async removeActivities(templateId: string, activityIds: string[]) {
      await TemplatesService.deleteActivities(templateId, {
        activity_ids: activityIds,
      });
      await useTemplateStore().loadTemplate(templateId);
    },

    async updateMilestone(
      templateId: string,
      activityId: string,
      milestoneId: string | null,
      required: boolean | null,
    ): Promise<boolean> {
      const milestone =
        milestoneId === null || required === null
          ? {
              id: null,
              required: null,
            }
          : {
              id: milestoneId,
              required: required,
            };

      return TemplatesService.updateActivity(templateId, activityId, {
        milestone,
      })
        .then(({ activity }) => this.write(templateId, activity))
        .then(() => true)
        .catch((err) => {
          console.error(err);

          return false;
        });
    },

    async addSuccessor(
      templateId: string,
      activityId: string,
      successorId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkAddSuccessor(
        templateId,
        activityId,
        successorId,
      );
      if (errors) {
        throw errors;
      }

      return TemplatesService.activityTemplateCreateSuccessor(
        templateId,
        activityId,
        { successor_id: successorId },
      ).then(() => {
        if (this.hasActivity(templateId, activityId)) {
          const activity = this.activity(templateId, activityId);
          if (activity) {
            activity.successors.push(successorId);
          }
        }
        if (this.hasActivity(templateId, successorId)) {
          const activity = this.activity(templateId, successorId);
          if (activity) {
            activity.predecessors.push(activityId);
          }
        }
        const summary = useTemplateStore().activitySummary(
          templateId,
          successorId,
        );
        summary.predecessors.push(activityId);
      });
    },

    async removeSuccessor(
      templateId: string,
      activityId: string,
      successorId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkRemoveSuccessor(
        templateId,
        activityId,
        successorId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateDeleteSuccessor(
        templateId,
        activityId,
        successorId,
      );

      const activity = this.activity(templateId, activityId);
      const successorIndex = activity.successors.findIndex(
        (suc) => suc === successorId,
      );
      if (successorIndex > -1) {
        activity.successors.splice(successorIndex, 1);
      }

      const summary = useTemplateStore().activitySummary(
        templateId,
        successorId,
      );
      summary.predecessors = summary.predecessors.filter(
        (id) => id !== activityId,
      );
    },

    async loadAvailableSuccessors(templateId: string, activityId: string) {
      try {
        const { activities } =
          await TemplatesService.getActivityAvailableSuccessors(
            templateId,
            activityId,
          );

        const activity = this.activity(
          templateId,
          activityId,
        ) as ActivityTemplate;

        const successorIds = activities.map((act) => act.id);
        activity.successors.length = 0;
        activity.successors.push(...successorIds);

        const availableSuccessors = activities;
        if (!availableSuccessors.length) {
          return false;
        }
        const templateStore = useTemplateStore();
        availableSuccessors.forEach((activity) => {
          templateStore.writeActivitySummary(templateId, activity);
        });
      } catch (error) {
        console.error(error);

        return false;
      }

      return true;
    },

    async addPredecessor(
      templateId: string,
      activityId: string,
      predecessorId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkAddPredecessor(
        templateId,
        predecessorId,
        activityId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateCreatePredecessor(
        templateId,
        activityId,
        { predecessor_id: predecessorId },
      );
      if (this.hasActivity(templateId, activityId)) {
        const activity = this.activity(templateId, activityId);
        activity.predecessors.push(predecessorId);
      }
      if (this.hasActivity(templateId, predecessorId)) {
        const activity = this.activity(templateId, predecessorId);
        activity.successors.push(activityId);
      }
      const summary = useTemplateStore().activitySummary(
        templateId,
        activityId,
      );
      summary.predecessors.push(predecessorId);
    },

    async removePredecessor(
      templateId: string,
      activityId: string,
      predecessorId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkRemovePredecessor(
        templateId,
        predecessorId,
        activityId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateDeletePredecessor(
        templateId,
        activityId,
        predecessorId,
      );
      if (this.hasActivity(templateId, activityId)) {
        const activity = this.activity(templateId, activityId);
        activity.predecessors = activity.predecessors.filter(
          (id) => id !== predecessorId,
        );
        activity.documents.forEach((doc) => {
          if (doc.input === predecessorId) {
            doc.input = null;
          }
        });
      }
      const summary = useTemplateStore().activitySummary(
        templateId,
        activityId,
      );
      summary.predecessors = summary.predecessors.filter(
        (id) => id !== predecessorId,
      );
    },

    async addTag(
      templateId: string,
      activityId: string,
      payload: ActivityTemplateTagCreate,
    ) {
      const errors = useActivityTemplateRulesStore().checkAddTagToActivity(
        templateId,
        activityId,
        payload,
      );
      if (errors) {
        throw errors;
      }

      const templateStore = useTemplateStore();
      const { tag } = await TemplatesService.activityTemplateCreateTagTemplate(
        templateId,
        activityId,
        payload,
      );
      await templateStore.fetchTags(templateId);
      const activity = this.activity(templateId, activityId);
      activity.tags.push(tag.id);
      templateStore.writeActivitySummary(templateId, activity);
    },

    async removeTag(templateId: string, activityId: string, tagId: string) {
      const errors = useActivityTemplateRulesStore().checkRemoveTagFromActivity(
        templateId,
        activityId,
        tagId,
      );
      if (errors) {
        throw errors;
      }
      const { removed_from_template } =
        await TemplatesService.activityTemplateDeleteTagTemplate(
          templateId,
          activityId,
          tagId,
        );
      const activity = this.activity(templateId, activityId);
      const tagIndex = activity.tags.findIndex((id) => id === tagId);
      if (tagIndex > -1) {
        activity.tags.splice(tagIndex, 1);
      }
      if (!removed_from_template) {
        return;
      }
      const templateStore = useTemplateStore();
      templateStore.writeActivitySummary(templateId, activity);
      await templateStore.fetchTags(templateId);
    },

    async updateActivitiesTags(
      templateId: string,
      payload: {
        activity_ids: string[];
        add?: string[];
        remove?: string[];
        add_new?: { title: string; topic: boolean }[];
      },
    ) {
      const templateStore = useTemplateStore();
      const { tags: newTags } = await TemplatesService.updateActivitiesTags(
        templateId,
        payload,
      );
      const { add, remove, activity_ids } = payload;

      // Fetch updated tags from template (new tags may have been created via add_new)
      await templateStore.fetchTags(templateId);

      // Optimistically update store(s)

      // Get IDs of newly created tags
      const newTagIds = newTags.map((tag) => tag.id);
      // Combine add tags with newly created tags
      const allTagsToAdd = [...(add || []), ...newTagIds];

      // Update each activity's tags
      activity_ids.forEach((activityId) => {
        // Update detailed activity if loaded in store
        this.writeActivityTags(templateId, activityId, {
          add: allTagsToAdd,
          remove,
        });

        // Update activity summary in template store
        templateStore.writeActivityTags(templateId, activityId, {
          add: allTagsToAdd,
          remove,
        });
      });
    },

    async createCompletionCriterion(
      templateId: string,
      activityId: string,
      payload: CompletionCriterionTemplateCreate,
      index?: number,
    ) {
      const errors =
        useActivityTemplateRulesStore().checkAddCompletionCriterion(
          templateId,
          activityId,
          index,
        );
      if (errors) {
        throw errors;
      }
      const { completion_criterion } =
        await TemplatesService.completionCriterionTemplateCreate(
          templateId,
          activityId,
          payload,
          index,
        );

      return this.writeCriterion(templateId, activityId, completion_criterion);
    },

    async deleteCompletionCriterion(
      templateId: string,
      activityId: string,
      completionCriterionId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkRemoveCC(
        templateId,
        activityId,
        completionCriterionId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateDeleteCompletionCriterionTemplate(
        templateId,
        activityId,
        completionCriterionId,
      );
      // From the map's summary too: going back to the map reloads nothing
      useTemplateStore().removeCriterionLocally(
        templateId,
        activityId,
        completionCriterionId,
      );
    },

    async updateCriterion(
      templateId: string,
      activityId: string,
      completion_criterion_id: string,
      payload: CompletionCriterionTemplateUpdate,
    ) {
      const errors = useActivityTemplateRulesStore().checkUpdateCC(
        templateId,
        activityId,
        completion_criterion_id,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const { completion_criterion } =
        await TemplatesService.updateCompletionCriterion(
          templateId,
          activityId,
          completion_criterion_id,
          payload,
        );

      return this.writeCriterion(templateId, activityId, completion_criterion);
    },

    async updateCompletionCriteriaOrder(
      templateId: string,
      activityId: string,
      order: string[],
    ) {
      const payload = {
        completion_criteria_order: order,
      };
      const errors = useActivityTemplateRulesStore().checkUpdateActivity(
        templateId,
        activityId,
        payload,
      );
      if (errors) {
        throw errors;
      }

      return TemplatesService.updateActivity(
        templateId,
        activityId,
        payload,
      ).then(({ activity }) => {
        this.write(templateId, activity);
        // The map lists the summary's copy; going back to it reloads nothing
        // (WI #5801)
        const templateStore = useTemplateStore();
        if (templateStore.hasTemplate(templateId)) {
          templateStore.writeActivitySummary(
            templateId,
            structuredClone(activity),
          );
        }
      });
    },

    // TODO: this API endpoint does not return anything
    // async fetchCompletionCriterion(
    //   templateId: string,
    //   activityId: number,
    //   completionCriterionId: number,
    // ) {
    //   try {
    //     // TODO: this API endpoint does not return anything
    //     const { completionCriterion } =
    //       await TemplatesService.activityTemplateGetCompletionCriterionTemplate(
    //         templateId,
    //         activityId,
    //         completionCriterionId,
    //       );
    //     const activity = this.activity(templateId, activityId);
    //   } catch (e) {
    //     console.error(e);
    //     return false;
    //   }
    // },

    async addApprover(
      templateId: string,
      activityId: string,
      approverId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkAddApprover(
        templateId,
        activityId,
        approverId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateAddApprover(
        templateId,
        activityId,
        { approver_id: approverId },
      );
      const activity = this.activity(
        templateId,
        activityId,
      ) as ActivityTemplate;
      activity.approvers.push(approverId);
    },

    async removeApprover(
      templateId: string,
      activityId: string,
      approverId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkRemoveApprover(
        templateId,
        activityId,
        approverId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateRemoveApprover(
        templateId,
        activityId,
        approverId,
      );
      const activity = this.activity(templateId, activityId);
      if (!activity) {
        throw new Error('missing acivity in store');
      }
      const approverIndex = activity.approvers.findIndex(
        (id) => id === approverId,
      );
      activity.approvers.splice(approverIndex, 1);
    },

    async addWatcher(
      templateId: string,
      activityId: string,
      watcherId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkAddWatcher(
        templateId,
        activityId,
        watcherId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateAddWatcher(
        templateId,
        activityId,
        { watcher_id: watcherId },
      );
      const activity = this.activity(templateId, activityId);
      activity.watchers.push(watcherId);
    },

    async removeWatcher(
      templateId: string,
      activityId: string,
      watcherId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkRemoveWatcher(
        templateId,
        activityId,
        watcherId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateRemoveWatcher(
        templateId,
        activityId,
        watcherId,
      );
      const activity = this.activity(templateId, activityId);
      const watcherIndex = activity.watchers.findIndex(
        (id) => id === watcherId,
      );
      if (watcherIndex > -1) {
        activity.watchers.splice(watcherIndex, 1);
      }
    },
    async getApprovers(templateId: string, activityId: string) {
      return await TemplatesService.getTemplateActivityApprovers(
        templateId,
        activityId,
      );
    },

    async addLinkToActivity(
      templateId: string,
      activityId: string,
      payload: ActivityTemplateLinkRequest,
    ) {
      const errors = useActivityTemplateRulesStore().checkAddLink(
        templateId,
        activityId,
        payload.link_id,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateAddLink(
        templateId,
        activityId,
        payload,
      );
      await useTemplateStore().fetchLinks(templateId);
      this.activity(templateId, activityId).links.push(payload.link_id);
    },

    async removeLinkFromActivity(
      templateId: string,
      activityId: string,
      linkId: string,
    ) {
      const errors = useActivityTemplateRulesStore().checkRemoveLink(
        templateId,
        activityId,
        linkId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateRemoveLink(
        templateId,
        activityId,
        linkId,
      );
      const activity = this.activity(templateId, activityId);
      const linkIndex = activity.links.findIndex((link) => link === linkId);
      if (linkIndex > -1) {
        activity.links.splice(linkIndex, 1);
      }
      await useTemplateStore().fetchLinks(templateId);
    },

    async loadDocuments(templateId: string, activityId: string) {
      const documents = await TemplatesService.getTemplateActivityDocuments(
        templateId,
        activityId,
      );
      const activity = this.activity(templateId, activityId);
      activity.documents = documents;
      this.syncSummaryDocuments(templateId, activityId);
    },

    async addDocumentToActivity(
      templateId: string,
      activityId: string,
      payload: ActivityDocumentTemplate,
    ) {
      const errors = useActivityTemplateRulesStore().checkAddActivityDocument(
        templateId,
        activityId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const { document: doc } =
        await TemplatesService.activityTemplateAddDocument(
          templateId,
          activityId,
          payload,
        );
      await useTemplateStore().fetchDocuments(templateId);
      this.activity(templateId, activityId)?.documents.push(doc);
      this.syncSummaryDocuments(templateId, activityId);
    },

    async updateActivityDocument(
      templateId: string,
      activityId: string,
      documentId: string,
      payload: UpdateActivityDocumentTemplate,
    ) {
      const errors =
        useActivityTemplateRulesStore().checkUpdateActivityDocument(
          templateId,
          activityId,
          documentId,
          payload,
        );
      if (errors) {
        throw errors;
      }
      const { document: doc } =
        await TemplatesService.activityTemplateUpdateDocument(
          templateId,
          activityId,
          documentId,
          payload,
        );
      this.writeDocument(templateId, activityId, doc);
    },

    async removeDocumentFromActivity(
      templateId: string,
      activityId: string,
      documentId: string,
    ) {
      const errors =
        useActivityTemplateRulesStore().checkRemoveActivityDocument(
          templateId,
          activityId,
          documentId,
        );
      if (errors) {
        throw errors;
      }
      await TemplatesService.activityTemplateRemoveDocument(
        templateId,
        activityId,
        documentId,
      );
      const activity = this.activity(
        templateId,
        activityId,
      ) as ActivityTemplate;
      const docIndex = activity.documents.findIndex(
        (doc) => doc.id === documentId,
      );
      if (docIndex > -1) {
        activity.documents.splice(docIndex, 1);
      }
      this.syncSummaryDocuments(templateId, activityId);
      await useTemplateStore().fetchDocuments(templateId);
    },

    async updateResponsible(
      templateId: string,
      activityId: string,
      responsibleId: string | null,
    ) {
      const payload = {
        responsible: responsibleId,
      };
      const errors = useActivityTemplateRulesStore().checkUpdateActivity(
        templateId,
        activityId,
        payload,
      );
      if (errors) {
        throw errors;
      }

      return TemplatesService.updateActivity(
        templateId,
        activityId,
        payload,
      ).then(({ activity }) => {
        this.write(templateId, activity);
      });
    },

    async updateDescription(
      templateId: string,
      activityId: string,
      newDescription: string,
    ): Promise<boolean> {
      try {
        const { activity } = await TemplatesService.updateActivity(
          templateId,
          activityId,
          { description: newDescription },
        );
        if (!activity) {
          throw Error('Something went wrong');
        }

        return true;
      } catch {
        return false;
      }
    },

    async loadActivityInputs(
      templateId: string,
      activityId: string,
      docId?: string,
    ) {
      const { documents } =
        await TemplatesService.getAvailableInputDocumentsForActivity(
          templateId,
          activityId,
          docId,
        );

      return documents;
    },

    async toLibrary(templateId: string, activityId: string) {
      const payload: CreateCompanyActivityRequest = {
        template_id: templateId,
        template_activity_id: activityId,
      };
      await useCompanyActivityStore().create(payload);
    },
  },
});
